import { Link } from 'react-router-dom';
import { PolicyLayout } from '../components/PolicyLayout';
import { formatScans, formatUsd, subscriptionPlan } from '../lib/pricing';
import { siteDetails } from '../lib/site';

export const Terms = () => (
  <PolicyLayout eyebrow="Legal" title="Terms of Service" updated="2 August 2026">
    <p>
      These Terms govern access to ADarveo, a self-service software platform for creating,
      publishing, and measuring browser-based augmented-reality experiences. By creating an account
      or completing an order, you agree to these Terms and our <Link to="/privacy">Privacy Policy</Link>.
    </p>

    <h2>1. The ADarveo service</h2>
    <p>
      ADarveo provides software for campaign and asset management, hosted scan experiences, QR-code
      generation, media delivery, and engagement analytics. ADarveo does not buy or place advertising,
      sell physical media, or provide agency, consulting, or managed marketing services as part of the
      subscription.
    </p>

    <h2>2. Accounts and authority</h2>
    <p>
      You must provide accurate account and billing information, keep credentials secure, and be
      authorized to act for the organization using the service. You are responsible for activity under
      your account and must promptly report suspected unauthorized access.
    </p>

    <h2>3. Subscription and included usage</h2>
    <p>
      ADarveo Pro costs {formatUsd(subscriptionPlan.monthlyPriceUsd)} USD per month and includes{' '}
      {formatScans(subscriptionPlan.includedScans)} scans in each monthly billing cycle. The subscription
      renews automatically each month until cancelled. Included scans reset when the next billing cycle
      begins and do not roll over.
    </p>
    <p>
      A billable scan is recorded when a viewer first successfully recognizes a configured target in a
      browser session. Landing-page visits and camera attempts without recognition are not intended to
      consume scan credits.
    </p>

    <h2>4. One-time scan packs</h2>
    <p>
      Scan packs are non-recurring usage purchases for customers with an active ADarveo subscription.
      Purchased pack credits remain valid for 12 months from the purchase date. Buying a pack does not
      change the subscription renewal price or create another recurring plan.
    </p>

    <h2>5. Orders, taxes, and Paddle</h2>
    <p>
      Prices are displayed in United States dollars unless the checkout states otherwise. Applicable
      taxes are calculated at checkout. Paddle.com acts as Merchant of Record for ADarveo orders and
      manages payment processing, payment-related buyer assistance, and returns. The checkout shows the
      product, billing frequency, taxes, and total payable before an order is confirmed.
    </p>
    <p>
      For payment receipts, payment-method changes, subscription cancellation, or transaction support,
      you may also use <a href="https://paddle.net" rel="noreferrer">Paddle buyer support</a>.
    </p>

    <h2>6. Cancellation and refunds</h2>
    <p>
      You may cancel the subscription before the next billing date to prevent renewal. Access continues
      through the paid billing period unless the account is terminated for misuse. Refund requests are
      handled under our <Link to="/refunds">Refund Policy</Link>, including its 30-day request window.
    </p>

    <h2>7. Customer content and permissions</h2>
    <p>
      You retain ownership of content you upload. You grant ADarveo the limited permission required to
      store, process, transform, and deliver that content to operate the service. You confirm that you
      have the necessary rights, releases, and permissions for all uploaded creative, trademarks,
      destination links, and campaign material.
    </p>

    <h2>8. Acceptable use</h2>
    <p>
      You may not use ADarveo for unlawful, deceptive, infringing, abusive, discriminatory, harmful, or
      fraudulent content; unauthorized tracking; malware; impersonation; or material that violates a
      third party’s rights. You must obtain any notices or consents required for your campaigns and may
      not attempt to bypass security, usage metering, or service limits.
    </p>

    <h2>9. Availability and analytics</h2>
    <p>
      Browser features, camera permissions, device compatibility, connectivity, and third-party
      services may affect an experience. Where recognition is unavailable, ADarveo may provide a video
      fallback. Analytics are operational measurements and may be affected by privacy controls,
      connectivity, duplicate filtering, or technical errors.
    </p>

    <h2>10. Suspension and termination</h2>
    <p>
      We may suspend access where reasonably necessary to protect the service, investigate misuse,
      comply with law, or address unpaid amounts. Where practical, we will provide notice and an
      opportunity to correct the issue.
    </p>

    <h2>11. Changes to these Terms</h2>
    <p>
      We may update these Terms as the service evolves. Material changes will be posted here with a new
      effective date. Continued use after an update takes effect constitutes acceptance of the revised
      Terms where permitted by law.
    </p>

    <h2>12. Contact</h2>
    <p>
      Questions about these Terms can be sent to{' '}
      <a href={`mailto:${siteDetails.supportEmail}`}>{siteDetails.supportEmail}</a>. The service is
      operated under the {siteDetails.legalName} brand.
    </p>
  </PolicyLayout>
);
